<template>
  <div class="perm-panel">
    <div class="perm-header">
      <strong>Permisos por agente</strong>
      <Button label="Actualizar" icon="pi pi-refresh" text size="small" :loading="loading" @click="loadPermissions" />
    </div>

    <Message v-if="errorMessage" severity="error" :closable="false">{{ errorMessage }}</Message>

    <div v-if="permissions.length" class="accordion-list">
      <details v-for="item in permissions" :key="item.agent_id" class="perm-accordion">
        <summary>
          <span>{{ item.agent_name }}</span>
          <Tag :value="item.is_overridden ? 'Personalizado' : 'Por rol'" :severity="item.is_overridden ? 'warning' : 'info'" />
        </summary>

        <div class="perm-grid">
          <label class="perm-option">
            <Checkbox v-model="item.can_view_documents" :binary="true" />
            <span>Ver documentos</span>
          </label>
          <label class="perm-option">
            <Checkbox v-model="item.can_view_results" :binary="true" />
            <span>Ver resultados</span>
          </label>
          <label class="perm-option">
            <Checkbox v-model="item.can_run_agent" :binary="true" />
            <span>Ejecutar agente</span>
          </label>
          <label class="perm-option">
            <Checkbox v-model="item.can_export_results" :binary="true" />
            <span>Exportar resultados</span>
          </label>
        </div>
      </details>
    </div>
    <p v-else-if="!loading" class="muted">No hay agentes activos para configurar permisos.</p>

    <div class="perm-actions">
      <Button label="Guardar permisos" icon="pi pi-save" :loading="saving" @click="savePermissions" />
    </div>

    <Toast />
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import api from '../../services/api';

const props = defineProps({
  userId: { type: Number, required: true },
});

const emit = defineEmits(['updated']);
const toast = useToast();
const loading = ref(false);
const saving = ref(false);
const errorMessage = ref('');
const permissions = ref([]);

const loadPermissions = async () => {
  loading.value = true;
  errorMessage.value = '';
  try {
    const { data } = await api.get(`/users/${props.userId}/agent-permissions`);
    permissions.value = (data.permissions || []).map((p) => ({ ...p }));
  } catch (err) {
    errorMessage.value = err?.response?.data?.detail || 'No se pudieron cargar los permisos.';
  } finally {
    loading.value = false;
  }
};

const savePermissions = async () => {
  saving.value = true;
  errorMessage.value = '';
  try {
    await api.put(`/users/${props.userId}/agent-permissions`, {
      permissions: permissions.value.map((p) => ({
        agent_id: p.agent_id,
        can_view_documents: !!p.can_view_documents,
        can_view_results: !!p.can_view_results,
        can_run_agent: !!p.can_run_agent,
        can_export_results: !!p.can_export_results,
        is_active: true,
      })),
    });
    toast.add({ severity: 'success', summary: 'Permisos guardados', life: 2500 });
    emit('updated');
    await loadPermissions();
  } catch (err) {
    errorMessage.value = err?.response?.data?.detail || 'No se pudieron guardar los permisos.';
  } finally {
    saving.value = false;
  }
};

onMounted(loadPermissions);
</script>

<style scoped>
.perm-panel {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.perm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.accordion-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.perm-accordion {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.perm-accordion summary {
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.75rem;
  font-weight: 600;
  color: #1f2937;
}

.perm-grid {
  border-top: 1px solid #e2e8f0;
  padding: 0.7rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.6rem;
}

.perm-option {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.88rem;
  color: #334155;
}

.perm-actions {
  display: flex;
  justify-content: flex-end;
}

.muted {
  color: #64748b;
}
</style>
